/**
 * useDependencyAnnotations — TanStack Query wrapper around the existing
 * `getDependencies` IPC. Surfaces a structured `DependencyAnnotations` payload
 * to the sidebar + ColumnsTab.
 *
 * Cache key: `['vqe-ddl-dependencies', connectionId, database, schema,
 * objectName, objectType]`.
 *
 * Invalidation: subscribes to `schemaBus` for the bound `connectionId` and
 * invalidates the query on `'invalidated'` / `'disconnected'` events. Mirrors
 * the pattern documented in `Ralph/VqeDdlEdit/PROGRESS.md` Codebase Patterns.
 *
 * Slice H2.
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { subscribe as subscribeSchemaBus } from '../../../intellisense/schemaBus/schemaBus';
import {
  buildDependencyAnnotations,
  emptyDependencyAnnotations,
} from './buildDependencyAnnotations';
import type { DependencyAnnotations, DependencyObjectType } from './types';

const FIVE_MINUTES = 5 * 60 * 1000;

export type UseDependencyAnnotationsParams = {
  readonly connectionId: string | null | undefined;
  readonly database: string | null | undefined;
  readonly schema: string | null | undefined;
  readonly objectName: string | null | undefined;
  readonly objectType: DependencyObjectType | null | undefined;
  /** When false, the query stays disabled regardless of the other params.
   *  Defaults to true. */
  readonly enabled?: boolean;
};

export const dependencyAnnotationsQueryKey = (
  connectionId: string | null | undefined,
  database: string | null | undefined,
  schema: string | null | undefined,
  objectName: string | null | undefined,
  objectType: DependencyObjectType | null | undefined,
) =>
  [
    'vqe-ddl-dependencies',
    connectionId ?? null,
    database ?? null,
    schema ?? null,
    objectName ?? null,
    objectType ?? null,
  ] as const;

export type UseDependencyAnnotationsResult = {
  readonly annotations: DependencyAnnotations;
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  /** True only when the wrapped query is a confirmed success for the current
   *  target. VDE-9 fail-safe: when this is false the `annotations` payload is
   *  an empty placeholder, NOT a verified "no dependents" answer — callers
   *  gating destructive ops must treat dependents as UNKNOWN.
   *  VDE-10: gating on `isSuccess` (rather than `!isStale && !isFetching`) is a
   *  deliberate, accepted trade-off — it tolerates a brief post-invalidation
   *  background-refetch window where `isSuccess===true` but `query.data` is the
   *  pre-invalidation snapshot, in exchange for not over-prompting on every
   *  routine `staleTime` refresh; do not "tighten" this. */
  readonly isSuccess: boolean;
  readonly error: Error | null;
  readonly query: UseQueryResult<DependencyAnnotations>;
};

export function useDependencyAnnotations(
  params: UseDependencyAnnotationsParams,
): UseDependencyAnnotationsResult {
  const { connectionId, database, schema, objectName, objectType, enabled = true } = params;
  const queryClient = useQueryClient();

  const queryEnabled =
    enabled === true &&
    typeof connectionId === 'string' && connectionId.length > 0 &&
    typeof database === 'string' && database.length > 0 &&
    typeof schema === 'string' && schema.length > 0 &&
    typeof objectName === 'string' && objectName.length > 0 &&
    typeof objectType === 'string';

  const query = useQuery<DependencyAnnotations>({
    queryKey: dependencyAnnotationsQueryKey(connectionId, database, schema, objectName, objectType),
    queryFn: async () => {
      // Type-narrowed by `queryEnabled`; defensive guard for renderer races.
      if (!connectionId || !database || !schema || !objectName || !objectType) {
        return emptyDependencyAnnotations();
      }
      const api = window.electronAPI?.getDependencies;
      if (!api) return emptyDependencyAnnotations();
      const result = await api({
        connectionId,
        database,
        schema,
        objectName,
        objectType,
      });
      if (!result.success || !result.dependencies) {
        if (result.error) throw new Error(result.error);
        return emptyDependencyAnnotations();
      }
      return buildDependencyAnnotations({
        uses: result.dependencies.uses,
        usedBy: result.dependencies.usedBy,
        limitedSupport: result.limitedSupport === true,
      });
    },
    enabled: queryEnabled,
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!connectionId) return;
    const unsubscribe = subscribeSchemaBus(connectionId, (event) => {
      if (event.type === 'invalidated' || event.type === 'disconnected') {
        // Prefix-match invalidation; mirror dependencyAnnotationsQueryKey's
        // `connectionId ?? null` normalization so the key shape stays consistent
        // (the !connectionId guard above already ensures a non-empty id here).
        queryClient.invalidateQueries({
          queryKey: ['vqe-ddl-dependencies', connectionId ?? null],
        });
      }
    });
    return unsubscribe;
  }, [connectionId, queryClient]);

  const annotations = query.data ?? emptyDependencyAnnotations();
  return {
    annotations,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
    error: query.error instanceof Error ? query.error : null,
    query,
  };
}
